import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { 
  ArrowLeft, Calculator, Plus, Trash2, Play, 
  BarChart3, Clock, Target, TrendingUp, ChevronRight
} from "lucide-react";
import { useAuth } from "../App";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function DrillConfig() {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [drills, setDrills] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  
  // Form state
  const [drillName, setDrillName] = useState("");
  const [selectedStudent, setSelectedStudent] = useState("");
  const [selectedTables, setSelectedTables] = useState([]);
  const [multiplierRange, setMultiplierRange] = useState(12);
  const [timeLimit, setTimeLimit] = useState(15);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Fetch drills
      const drillsRes = await axios.get(`${API}/drills`);
      setDrills(drillsRes.data.drills || []);
      
      // Fetch linked students (for parents)
      if (user?.role === "parent") {
        const studentsRes = await axios.get(`${API}/parent/students`);
        if (studentsRes.data) {
          setStudents(studentsRes.data);
        }
      }
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleTableToggle = (tableNum) => {
    setSelectedTables(prev => 
      prev.includes(tableNum)
        ? prev.filter(t => t !== tableNum)
        : [...prev, tableNum]
    );
  };

  const handleSelectRange = (start, end) => {
    const range = [];
    for (let i = start; i <= end; i++) {
      range.push(i);
    }
    setSelectedTables(range);
  };

  const handleCreateDrill = async () => {
    if (!drillName.trim()) {
      toast.error("Please enter a drill name");
      return;
    }
    if (!selectedStudent) {
      toast.error("Please select a student");
      return;
    }
    if (selectedTables.length === 0) {
      toast.error("Please select at least one multiplication table");
      return;
    }
    
    setCreating(true);
    try {
      await axios.post(`${API}/drills`, {
        name: drillName.trim(),
        drill_type: "multiplication",
        student_id: selectedStudent,
        tables: selectedTables.sort((a, b) => a - b),
        multiplier_range: multiplierRange,
        initial_seconds: timeLimit
      });
      
      toast.success("Drill created successfully!");
      setShowCreateForm(false);
      setDrillName("");
      setSelectedTables([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create drill");
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteDrill = async (drillId) => {
    if (!window.confirm("Delete this drill and all its session history?")) return;
    
    try {
      await axios.delete(`${API}/drills/${drillId}`);
      toast.success("Drill deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete drill");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-12 h-12 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Math Drills</h1>
              <p className="text-sm text-gray-500">Configure multiplication practice for your children</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6">
        {/* Create New Drill Button */}
        {!showCreateForm && (
          <Button 
            onClick={() => setShowCreateForm(true)}
            className="mb-6 gap-2"
          >
            <Plus className="w-4 h-4" />
            Create New Drill
          </Button>
        )}

        {/* Create Form */}
        {showCreateForm && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calculator className="w-5 h-5 text-indigo-600" />
                  Create New Drill
                </CardTitle>
                <CardDescription>
                  Configure a multiplication drill for your child
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Drill Name */}
                <div>
                  <Label htmlFor="drillName">Drill Name</Label>
                  <Input
                    id="drillName"
                    value={drillName}
                    onChange={(e) => setDrillName(e.target.value)}
                    placeholder="e.g., Times Tables 13-15"
                    className="mt-1"
                  />
                </div>

                {/* Select Student */}
                <div>
                  <Label>Select Student</Label>
                  <Select value={selectedStudent} onValueChange={setSelectedStudent}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Choose a student" />
                    </SelectTrigger>
                    <SelectContent>
                      {students.map((student) => (
                        <SelectItem key={student.user_id} value={student.user_id}>
                          {student.display_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Multiplier Range */}
                <div>
                  <Label>Multiply up to</Label>
                  <Select 
                    value={multiplierRange.toString()} 
                    onValueChange={(v) => setMultiplierRange(parseInt(v))}
                  >
                    <SelectTrigger className="mt-1 w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[10, 12, 15, 20, 25].map((n) => (
                        <SelectItem key={n} value={n.toString()}>× 1 to {n}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Time Limit Selection */}
                <div>
                  <Label>Time per Question (Start)</Label>
                  <Select 
                    value={timeLimit.toString()} 
                    onValueChange={(v) => setTimeLimit(parseInt(v))}
                  >
                    <SelectTrigger className="mt-1 w-full sm:w-64">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="3">3 seconds (Expert)</SelectItem>
                      <SelectItem value="5">5 seconds (Advanced)</SelectItem>
                      <SelectItem value="10">10 seconds (Standard)</SelectItem>
                      <SelectItem value="15">15 seconds (Relaxed)</SelectItem>
                      <SelectItem value="20">20 seconds (Extra Time)</SelectItem>
                      <SelectItem value="30">30 seconds (Learning)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500 mt-1">
                    Time will adjust automatically based on performance, up to this maximum.
                  </p>
                </div>

                {/* Table Selection */}
                <div>
                  <Label className="mb-2 block">Select Multiplication Tables (1-25)</Label>
                  
                  {/* Quick select buttons */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleSelectRange(1, 10)}
                    >
                      1-10
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleSelectRange(11, 15)}
                    >
                      11-15
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleSelectRange(13, 16)}
                    >
                      13-16
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleSelectRange(1, 25)}
                    >
                      All (1-25)
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setSelectedTables([])}
                    >
                      Clear
                    </Button>
                  </div>
                  
                  {/* Table grid */}
                  <div className="grid grid-cols-5 sm:grid-cols-10 gap-2">
                    {Array.from({ length: 25 }, (_, i) => i + 1).map((num) => (
                      <button
                        key={num}
                        onClick={() => handleTableToggle(num)}
                        className={`
                          w-10 h-10 rounded-lg font-medium transition-all
                          ${selectedTables.includes(num)
                            ? 'bg-indigo-600 text-white shadow-md'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }
                        `}
                      >
                        {num}
                      </button>
                    ))}
                  </div>
                  
                  {selectedTables.length > 0 && (
                    <p className="text-sm text-gray-500 mt-2">
                      Selected: {selectedTables.sort((a, b) => a - b).join(", ")} 
                      ({selectedTables.length * multiplierRange} questions total)
                    </p>
                  )}
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-4">
                  <Button 
                    onClick={handleCreateDrill}
                    disabled={creating}
                    className="gap-2"
                  >
                    {creating ? "Creating..." : "Create Drill"}
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => setShowCreateForm(false)}
                  >
                    Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Existing Drills */}
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Your Drills</h2>
          
          {drills.length === 0 ? (
            <Card className="text-center py-12">
              <CardContent>
                <Calculator className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No drills created yet</p>
                <p className="text-sm text-gray-400">Create a drill to help your child practice multiplication</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {drills.map((drill) => (
                <motion.div
                  key={drill.drill_id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  <Card className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="font-semibold text-gray-900">{drill.name}</h3>
                            <Badge variant="outline">{drill.student_display_name}</Badge>
                          </div>
                          
                          <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                            <span className="flex items-center gap-1">
                              <Target className="w-4 h-4" />
                              Tables: {drill.tables?.join(", ")}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {drill.stats?.current_time_per_question || 10}s per question
                            </span>
                            <span className="flex items-center gap-1">
                              <BarChart3 className="w-4 h-4" />
                              {drill.stats?.sessions_completed || 0} sessions
                            </span>
                            {drill.stats?.avg_accuracy > 0 && (
                              <span className="flex items-center gap-1">
                                <TrendingUp className="w-4 h-4" />
                                {drill.stats.avg_accuracy}% avg
                              </span>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate(`/drills/${drill.drill_id}/stats`)}
                          >
                            <BarChart3 className="w-4 h-4 mr-1" />
                            Stats
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleDeleteDrill(drill.drill_id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
